import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setCategoryOpen(false);
    setMobileMenuOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';
  const navBgClass = scrolled || !isHome
    ? 'bg-background-50 border-b border-background-200/70'
    : 'bg-transparent';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Left nav links */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname === '/'
                  ? 'text-primary-500'
                  : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              首页
            </Link>

            {/* Category dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname.startsWith('/category')
                    ? 'text-primary-500'
                    : 'text-foreground-700 hover:text-primary-500'
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${categoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {categoryOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-background-50 rounded-lg border border-background-200/70 py-2 shadow-lg">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-base w-4 h-4 flex items-center justify-center`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/category/hanman"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname === '/category/hanman'
                  ? 'text-primary-500'
                  : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              排行
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-foreground-800`}></i>
          </button>

          {/* Center: Brand */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-book-read-line text-lg text-background-50"></i>
            </div>
            <span className="text-lg font-bold text-foreground-950 whitespace-nowrap" style={{ fontFamily: "var(--font-heading)" }}>
              酷漫屋
            </span>
          </Link>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <button className="hidden md:flex w-9 h-9 items-center justify-center rounded-full hover:bg-background-100 transition-colors duration-200 cursor-pointer">
              <i className="ri-search-line text-lg text-foreground-600"></i>
            </button>
            <button className="hidden md:flex w-9 h-9 items-center justify-center rounded-full hover:bg-background-100 transition-colors duration-200 cursor-pointer">
              <i className="ri-user-line text-lg text-foreground-600"></i>
            </button>
            <Link
              to="/category/hanman"
              className="hidden md:flex items-center gap-1.5 px-5 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              开始阅读
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 py-4 px-2">
            <div className="flex flex-col gap-1">
              <Link to="/" className="px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors duration-150 whitespace-nowrap">
                首页
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors duration-150 whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-base w-4 h-4 flex items-center justify-center`}></i>
                  {cat.name}
                </Link>
              ))}
              <Link to="/category/hanman" className="px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors duration-150 whitespace-nowrap">
                排行
              </Link>
              <Link
                to="/category/hanman"
                className="mx-4 mt-2 flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                开始阅读
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}